import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import type {
  BudgetSheetState,
  MetricSheetState,
  TechnicalSpecificationState
} from '../../../../../../../services/projectAutofillDraftService.ts'
import {
  extractRubroMetricWorkflowFields,
  parseMetricEligibility,
  parseQuotationEligibility,
  parseTechnicalSpecEligibility,
  resolveMetricComputationView
} from '../../../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'
import type {
  CommittedDraftSnapshot,
  DraftValidationShape,
  ProjectAutofillPayload,
  RubroRow
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  computeRecommendedQuotationWindow,
  normalizeRecommendedQuotationWindow
} from '../../rubroQuotationTimelineUtils.ts'
import {
  clampIsoDateToMax,
  DOCUMENTATION_SLOT_KEYS,
  documentationSlotSourcesHaveData,
  documentationSlotsHavePages,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  normalizeDocumentationSlotSources,
  normalizePageList,
  serializeRubrosSnapshot
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { toHtmlDateInputValue } from '../../../../../../../utils/toHtmlDateInputValue.ts'

type SaveState = 'idle' | 'ok' | 'error'

export type UseProjectAutofillPayloadHydrationArgs = {
  payload: ProjectAutofillPayload
  committedRef: MutableRefObject<CommittedDraftSnapshot | null>
  setName: Dispatch<SetStateAction<string>>
  setDescription: Dispatch<SetStateAction<string>>
  setZone: Dispatch<SetStateAction<string>>
  setStartDate: Dispatch<SetStateAction<string>>
  setEndDate: Dispatch<SetStateAction<string>>
  setRubros: Dispatch<SetStateAction<RubroRow[]>>
  setFuzzyConfirmByRubro: Dispatch<SetStateAction<Record<string, boolean>>>
  setPatchedDraftSync: Dispatch<
    SetStateAction<{
      validation?: DraftValidationShape
      collectedData?: ProjectAutofillPayload['collectedData']
    } | null>
  >
  setExpandedRubros: Dispatch<SetStateAction<Record<string, boolean>>>
  setSaveState: Dispatch<SetStateAction<SaveState>>
}

export function useProjectAutofillPayloadHydration(args: UseProjectAutofillPayloadHydrationArgs) {
  const {
    payload,
    committedRef,
    setName,
    setDescription,
    setZone,
    setStartDate,
    setEndDate,
    setRubros,
    setFuzzyConfirmByRubro,
    setPatchedDraftSync,
    setExpandedRubros,
    setSaveState
  } = args

  useEffect(() => {
    const nextName = payload.draft?.project?.name || ''
    const nextDescription = payload.draft?.project?.description || ''
    const nextZone = payload.draft?.project?.zone || ''
    const nextStart = toHtmlDateInputValue(payload.draft?.project?.startDate ?? '')
    const nextEnd = toHtmlDateInputValue(payload.draft?.project?.endDate ?? '')
    setName(nextName)
    setDescription(nextDescription)
    setZone(nextZone)
    setStartDate(nextStart)
    setEndDate(nextEnd)
    const docPreview = payload.draft?.metadata?.documentationPreview
    const nextRubros = (payload.draft?.subprojects || []).map((sp, idx) => {
      let s = toHtmlDateInputValue(sp.startDate || '') || ''
      let e = toHtmlDateInputValue(sp.endDate || '') || ''
      if (nextEnd) {
        if (s) s = clampIsoDateToMax(s, nextEnd)
        if (e) e = clampIsoDateToMax(e, nextEnd)
      }
      if (s && e && e < s) e = s
      const tid = sp.tempId || `rubro-${idx}`
      const spRec = sp as Record<string, unknown>
      const slotsFromSp = normalizeDocumentationSlots(sp.documentationSlots)
      let documentationSlots = slotsFromSp
      let sourcePagesNorm = normalizePageList(sp.sourcePages)
      let sourceFileId = spRec.sourceFileId != null ? String(spRec.sourceFileId).trim() : ''
      let documentationSlotSources = normalizeDocumentationSlotSources(spRec.documentationSlotSources)
      const assignmentForRubro = docPreview?.assignments?.find(
        (x) => String(x?.tempId ?? '') === String(tid)
      )
      if (assignmentForRubro && !documentationSlotsHavePages(slotsFromSp)) {
        documentationSlots = normalizeDocumentationSlots(assignmentForRubro.documentationSlots)
        if (!sourcePagesNorm?.length) {
          sourcePagesNorm = normalizePageList(assignmentForRubro.sourcePages)
        }
      }
      if (!sourceFileId && assignmentForRubro?.sourceFileId) {
        sourceFileId = String(assignmentForRubro.sourceFileId).trim()
      }
      if (!documentationSlotSourcesHaveData(documentationSlotSources) && assignmentForRubro) {
        documentationSlotSources = normalizeDocumentationSlotSources(
          assignmentForRubro.documentationSlotSources
        )
      }
      const sourceFileIdOut = sourceFileId || undefined
      const documentationSlotSourcesOut = documentationSlotSourcesHaveData(documentationSlotSources)
        ? documentationSlotSources
        : undefined
      const matchedSkillNameFromBack =
        String(sp.matchedSkillName || '').trim() ||
        String(assignmentForRubro?.matchedSkillName || '').trim() ||
        undefined
      const dfIds = normalizeDocumentationFileIds(sp.documentationFileIds ?? {})
      const row: RubroRow = {
        tempId: tid,
        detectedLabel: sp.detectedLabel || '',
        selectedSkillId: sp.selectedSkillId,
        description: sp.description || '',
        status: (sp.status as 'OPEN' | 'CLOSE') || 'OPEN',
        skillMatchKind: sp.skillMatchKind ?? assignmentForRubro?.skillMatchKind,
        matchedSkillName: matchedSkillNameFromBack,
        matchScore: sp.matchScore,
        name: sp.name,
        link: sp.link,
        startDate: s,
        endDate: e,
        datesInheritedFromProject: sp.datesInheritedFromProject,
        recommendedQuotationWindow:
          normalizeRecommendedQuotationWindow(spRec.recommendedQuotationWindow) ??
          computeRecommendedQuotationWindow(nextStart, nextEnd) ??
          undefined,
        documentationSlots,
        sourcePages: sourcePagesNorm,
        ...(sourceFileIdOut ? { sourceFileId: sourceFileIdOut } : {}),
        ...(documentationSlotSourcesOut ? { documentationSlotSources: documentationSlotSourcesOut } : {}),
        ...(DOCUMENTATION_SLOT_KEYS.some((k) => !!dfIds[k]) ? { documentationFileIds: dfIds } : {}),
        ...(sp.budgetSheetState != null &&
        typeof sp.budgetSheetState === 'object' &&
        !Array.isArray(sp.budgetSheetState)
          ? { budgetSheetState: sp.budgetSheetState as BudgetSheetState }
          : {}),
        ...(sp.metricSheetState != null &&
        typeof sp.metricSheetState === 'object' &&
        !Array.isArray(sp.metricSheetState)
          ? { metricSheetState: sp.metricSheetState as MetricSheetState }
          : {}),
        ...extractRubroMetricWorkflowFields(spRec),
        metricEligibility: parseMetricEligibility(spRec.metricEligibility) ?? undefined,
        quotationEligibility: parseQuotationEligibility(spRec.quotationEligibility) ?? undefined,
        ...(spRec.technicalSpecificationState != null &&
        typeof spRec.technicalSpecificationState === 'object' &&
        !Array.isArray(spRec.technicalSpecificationState)
          ? {
              technicalSpecificationState: spRec.technicalSpecificationState as TechnicalSpecificationState
            }
          : {}),
        ...(spRec.technicalSpecificationSuggestionStatus === 'generated' ||
        spRec.technicalSpecificationSuggestionStatus === 'insufficient'
          ? {
              technicalSpecificationSuggestionStatus: spRec.technicalSpecificationSuggestionStatus as
                | 'generated'
                | 'insufficient'
            }
          : {}),
        ...(typeof spRec.technicalSpecificationSuggestionReason === 'string' &&
        spRec.technicalSpecificationSuggestionReason.trim()
          ? {
              technicalSpecificationSuggestionReason: String(
                spRec.technicalSpecificationSuggestionReason
              ).trim()
            }
          : {}),
        technicalSpecEligibility: parseTechnicalSpecEligibility(spRec.technicalSpecEligibility) ?? undefined
      }
      const view = resolveMetricComputationView(row)
      row.metricComputationView = view
      if (view !== 'legajo') row.metricLegajo = undefined
      if (!row.canImportMetricEdifySheet) row.canImportMetricEdifySheet = undefined
      return row
    })
    setRubros(nextRubros)
    setFuzzyConfirmByRubro({})
    setPatchedDraftSync(null)
    committedRef.current = {
      name: nextName,
      description: nextDescription,
      zone: nextZone,
      startDate: nextStart,
      endDate: nextEnd,
      rubrosKey: serializeRubrosSnapshot(nextRubros),
      rubrosCount: nextRubros.length
    }
    setSaveState('idle')
    setExpandedRubros((prev) => {
      const next: Record<string, boolean> = {}
      nextRubros.forEach((r) => {
        if (prev[r.tempId]) next[r.tempId] = true
      })
      return next
    })
  }, [
    payload.draft?.project?.name,
    payload.draft?.project?.description,
    payload.draft?.project?.zone,
    payload.draft?.project?.startDate,
    payload.draft?.project?.endDate,
    payload.draft?.subprojects,
    payload.draft?.metadata?.documentationPreview,
    setName,
    setDescription,
    setZone,
    setStartDate,
    setEndDate,
    setRubros,
    setFuzzyConfirmByRubro,
    setPatchedDraftSync,
    committedRef,
    setSaveState,
    setExpandedRubros
  ])
}
